import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const VARIANTS: Record<ButtonVariant, string> = {
    primary:
        'bg-brand-600 text-white shadow-sm shadow-brand-900/20 hover:bg-brand-700 focus-visible:outline-brand-600',
    secondary:
        'border border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800',
    ghost:
        'text-brand-700 hover:bg-brand-50 focus-visible:outline-brand-600 dark:text-brand-300 dark:hover:bg-brand-950',
    danger: 'bg-rose-600 text-white hover:bg-rose-700 focus-visible:outline-rose-600'
};

const SIZES = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-5 py-3 text-base'
};

/**
 * Every button in the portal and on the account screens.
 *
 * **The brand teal replaces the `sky` blue these screens were built in.** The palette is shared with
 * WebAdmin's PrimeNG preset and with the live site, and a customer portal in a colour that appears
 * nowhere else on the site reads as a different product — which for a page holding somebody's
 * mortgage file is the wrong thing to suggest.
 *
 * **`disabled` is dimmed *and* keeps its cursor**, because the ones that disable here are the ones
 * mid-request: an upload, a sign-in, a message being sent. A control that vanishes into 60% opacity
 * with no other change looks broken rather than busy, so the label says what is happening and the
 * caller passes it in.
 */
export function Button({
    variant = 'primary',
    size = 'md',
    block = false,
    className = '',
    children,
    ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: ButtonVariant;
    size?: keyof typeof SIZES;
    block?: boolean;
    children: ReactNode;
}) {
    return (
        <button
            {...rest}
            className={`inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-55 ${VARIANTS[variant]} ${SIZES[size]} ${block ? 'w-full' : ''} ${className}`}
        >
            {children}
        </button>
    );
}
